/**
 * Small inputs the sections share: text in the brand's languages (the default first, the rest
 * behind "Other languages"), a moment in a video as 12:30, a group's label and its error. Each
 * input carries the DOM id of its path, so an error summary or Review & publish can open it.
 */

import { useEffect, useId, useState } from "react";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import { fieldDomId } from "../paths";
import { clock, parseClock } from "../time";
import { useForm } from "../form-context";

type Localized = Record<string, string>;

export function LocalizedField({ path, label, multiline, required, help, value, onChange, size, hideLabel }: {
  path: string;
  label: string;
  multiline?: boolean;
  required?: boolean;
  help?: string;
  /** Defaults to the form's value at `path` and setting it. */
  value?: Localized;
  onChange?: (v: Localized | undefined) => void;
  size?: "sm" | "md";
  hideLabel?: boolean;
}) {
  const form = useForm();
  const current = value ?? ((form.doc ? getLocal(form.doc, path) : undefined) ?? {});
  const others = form.locales.filter((l) => l !== form.locale);
  const [open, setOpen] = useState(() => others.some((l) => current[l]));
  const write = (locale: string, text: string) => {
    const next = { ...current, [locale]: text };
    if (!text) delete next[locale];
    const out = Object.keys(next).length ? next : undefined;
    if (onChange) onChange(out); else form.set(path, out);
  };
  const error = form.issue(path)?.message;
  return (
    <div className="flex flex-col gap-2">
      <Field id={fieldDomId(path)} label={hideLabel ? undefined : label} aria-label={hideLabel ? label : undefined} textarea={multiline} size={size} value={current[form.locale] ?? ""} optional={!required && !hideLabel} description={help} error={error}
        onChange={(e) => write(form.locale, e.target.value)} onBlur={() => form.touch(path)} disabled={!form.canEdit} />
      {others.length ? (
        open ? (
          <div className="flex flex-col gap-2 border-s-2 border-s-subtle ps-3">
            {others.map((l) => (
              <Field key={l} label={`${label} · ${languageName(l)}`} size="sm" textarea={multiline} optional value={current[l] ?? ""} onChange={(e) => write(l, e.target.value)} disabled={!form.canEdit} />
            ))}
          </div>
        ) : (
          <div><Button type="button" size="xs" variant="ghost" onClick={() => setOpen(true)}>{`Other languages (${others.map((l) => l.toUpperCase()).join(", ")})`}</Button></div>
        )
      ) : null}
    </div>
  );
}

function getLocal(doc: Record<string, unknown>, path: string): Localized | undefined {
  let at: unknown = doc;
  for (const part of path.split(".")) { if (!at || typeof at !== "object") return undefined; at = (at as Record<string, unknown>)[part]; }
  return at && typeof at === "object" ? (at as Localized) : undefined;
}

export function languageName(locale: string): string {
  try { return new Intl.DisplayNames([locale], { type: "language" }).of(locale) ?? locale.toUpperCase(); } catch { return locale.toUpperCase(); }
}

/** A moment as 12:30 (or 1:02:30); stored as seconds. Checked when the editor leaves it. */
export function ClockField({ id, label, value, onChange, optional, error, size = "sm", placeholder = "0:00" }: { id?: string; label: string; placeholder?: string; value: number | undefined; onChange: (v: number | undefined) => void; optional?: boolean; error?: string; size?: "sm" | "md" }) {
  const [textValue, setText] = useState(value === undefined ? "" : clock(value));
  const [bad, setBad] = useState("");
  useEffect(() => { setText(value === undefined ? "" : clock(value)); }, [value]);
  return (
    <Field id={id} label={label} size={size} optional={optional} inputMode="numeric" placeholder={placeholder} value={textValue} error={bad || error}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (!textValue.trim()) { setBad(""); onChange(undefined); return; }
        const s = parseClock(textValue);
        if (s === null) { setBad("Write it as minutes and seconds, e.g. 12:30."); return; }
        setBad(""); onChange(s);
      }} />
  );
}

/** A labelled group of inputs with its own description and error (fieldset + legend). */
export function Group({ path, label, help, optional, children, error }: { path?: string; label: string; help?: string; optional?: boolean; children: React.ReactNode; error?: string }) {
  const id = useId();
  return (
    <fieldset id={path ? fieldDomId(path) : undefined} tabIndex={path ? -1 : undefined} className="flex min-w-0 flex-col gap-3 outline-none" aria-describedby={help ? `${id}-help` : undefined}>
      <legend className="mb-2 flex items-center gap-1.5">
        <span className="textfield__label">{label}</span>
        {optional ? <span className="textfield__optional">Optional</span> : null}
      </legend>
      {help ? <p id={`${id}-help`} className="-mt-2 text-caption text-t-secondary">{help}</p> : null}
      {children}
      {error ? <p className="textfield__error" role="alert">{error}</p> : null}
    </fieldset>
  );
}
